import React, { useEffect, useRef, useState } from 'react';
import { api, ApiClientError } from '../../core/apiClient.ts';
import type { OAuthUrlResponse, SocialAccount } from '../../core/types.ts';
import { refreshAllPages } from '../ad-assets/pageSync.ts';
import { useOAuthStatus } from './useOAuthStatus.ts';

export function RefreshTokenModal({ account, onClose, onUpdated }: { account: SocialAccount; onClose: () => void; onUpdated: (type: 'success' | 'error', message: string) => void }) {
  const [mode, setMode] = useState<'login' | 'manual'>('manual');
  const [token, setToken] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [link, setLink] = useState<OAuthUrlResponse | null>(null);
  const [copied, setCopied] = useState(false);
  const dialog = useRef<HTMLElement>(null);
  const status = useOAuthStatus(link?.sessionId, link?.expiresAt);
  const reported = useRef<string>();
  const waiting = !!link && (!status || ['pending', 'processing'].includes(status.status));
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    return () => previous?.focus();
  }, []);
  useEffect(() => {
    if (status?.status === 'success' && reported.current !== link?.sessionId) {
      reported.current = link?.sessionId;
      setBusy(true);
      void refreshAllPages(account.id).then((pages) => {
        onUpdated(pages.ok ? 'success' : 'error', pages.ok
          ? `Довготривалий токен для «${account.name ?? account.id}» і токени сторінок оновлено`
          : `Довготривалий токен оновлено. Не вдалося оновити токени сторінок: ${pages.errors.join('; ')}`);
        onClose();
      }).finally(() => setBusy(false));
    }
  }, [status, link, account, onUpdated, onClose]);
  useEffect(() => {
    const handler = (event: KeyboardEvent) => { if (event.key === 'Escape' && !busy) onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [busy, onClose]);
  const copy = async (url: string) => {
    try { await navigator.clipboard.writeText(url); setCopied(true); }
    catch { setCopied(false); setError('Не вдалося скопіювати. Скопіюйте посилання з поля нижче.'); }
  };
  const generate = async () => {
    setBusy(true); setError(''); setCopied(false);
    try {
      const next = await api.post<OAuthUrlResponse>(`/api/accounts/${encodeURIComponent(account.id)}/oauth-url`, {});
      setLink(next);
      await copy(next.url);
    } catch (err) { setError(err instanceof Error ? err.message : 'Не вдалося створити посилання'); }
    finally { setBusy(false); }
  };
  const save = async () => {
    setBusy(true); setError('');
    try {
      await api.put(`/api/accounts/${encodeURIComponent(account.id)}/access-token`, { access_token: token.trim() });
      onUpdated('success', `Тимчасовий токен для «${account.name ?? account.id}» збережено. Після запуску поверніть постійний токен у картці акаунта.`);
      onClose();
    } catch (err) { setError(err instanceof ApiClientError ? err.message : 'Не вдалося оновити токен'); }
    finally { setBusy(false); }
  };
  return <div className="modal-overlay" onClick={() => { if (!busy) onClose(); }}>
    <section ref={dialog} className="modal-box token-refresh" role="dialog" aria-modal="true" aria-labelledby="refresh-title" onClick={(event) => event.stopPropagation()} onKeyDown={(event) => {
      if (event.key !== 'Tab') return;
      const items = [...(dialog.current?.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled), a[href]') ?? [])];
      const first = items[0], last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    }}>
      <div className="modal-header"><div><h3 id="refresh-title">Оновити токен</h3><p className="text-sm text-muted">{account.name ?? account.id}</p></div><button autoFocus className="btn btn--ghost btn--sm" aria-label="Закрити" disabled={busy} onClick={onClose}>×</button></div>
      <div className="token-refresh__modes">
        <button className={`filter-chip ${mode === 'manual' ? 'filter-chip--active' : ''}`} disabled={busy || waiting} onClick={() => setMode('manual')}>Graph API Explorer</button>
        <button className={`filter-chip ${mode === 'login' ? 'filter-chip--active' : ''}`} disabled={busy || waiting} onClick={() => setMode('login')}>Повторна авторизація</button>
      </div>
      {mode === 'login' ? <>
        <p className="text-sm text-muted">Створіть посилання та відкрийте його в браузері потрібного Facebook акаунта. CRM дочекається авторизації й оновить довготривалий токен.</p>
        {link && <div className="token-refresh__waiting" role="status">
          <strong>{status?.status === 'success' ? 'Авторизація успішна. Оновлюємо токени сторінок…' : waiting ? 'Очікуємо авторизацію…' : status?.status === 'expired' ? 'Посилання прострочене' : 'Авторизацію не завершено'}</strong>
          <p className="text-sm">{status?.message ?? (copied ? 'Посилання скопійовано. Вставте його у вкладку потрібного браузера.' : 'Відкрийте посилання у потрібному браузері.')}</p>
          <input className="input" readOnly value={link.url} aria-label="Посилання для авторизації" onFocus={(event) => event.target.select()} />
          <button className="btn btn--secondary btn--sm" onClick={() => { setError(''); void copy(link.url); }}>Копіювати посилання</button>
          <p className="text-xs text-muted">Посилання діє 10 хвилин. Не закривайте це вікно до завершення.</p>
        </div>}
        <div className="modal-actions"><button className="btn btn--primary" disabled={busy || waiting} onClick={generate}>{busy ? 'Створюємо…' : link ? 'Створити нове посилання' : 'Створити та скопіювати посилання'}</button></div>
      </> : <>
        <p className="text-sm text-muted">Токен із Graph API Explorer використовується лише для запуску кампаній після підозрілої активності Meta. Постійний токен і доступи сторінок зберігаються. Після запуску натисніть «Повернути постійний токен» у картці акаунта.</p>
        <a className="btn btn--secondary btn--sm" href="https://developers.facebook.com/tools/explorer/" target="_blank" rel="noreferrer">Відкрити Graph API Explorer ↗</a>
        <button className="btn btn--ghost btn--sm" onClick={() => { void navigator.clipboard.writeText('https://developers.facebook.com/tools/explorer/').catch(() => setError('Не вдалося скопіювати посилання')); }}>Копіювати Explorer</button>
        <label className="token-refresh__field">Access token<input className="input" type="password" autoComplete="off" value={token} onChange={(event) => setToken(event.target.value)} placeholder="Вставте новий токен" /></label>
        <div className="modal-actions"><button className="btn btn--primary" disabled={busy || !token.trim()} onClick={save}>{busy ? 'Зберігаємо…' : 'Увімкнути тимчасовий токен'}</button></div>
      </>}
      {error && <p className="notification notification--error" role="alert">{error}</p>}
    </section>
  </div>;
}
